import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';

interface LoginProps {
  onLoginSuccess: (token: string) => void;
}

const Login: React.FC<LoginProps> = ({ onLoginSuccess }) => {
  const [loginMode, setLoginMode] = useState<'credentials' | 'token'>('credentials');
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleLoginWithToken = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!token.trim()) {
      setError('Please enter a token');
      return;
    }

    // Store token and proceed
    localStorage.setItem('accessToken', token);
    localStorage.setItem('authToken', token);
    onLoginSuccess(token);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!login.trim() || !password.trim()) {
      setError('Please enter both email and password');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('🔐 Submitting login request...');
      const response = await fetch('/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ login, password }),
      });

      const contentType = response.headers.get('content-type');
      console.log(`📡 Response status: ${response.status}, content-type: ${contentType}`);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Login error response:', errorText.substring(0, 200));
        throw new Error(`Login failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      if (data.accessToken) {
        // Store JWT in localStorage
        localStorage.setItem('accessToken', data.accessToken);
        localStorage.setItem('authToken', data.accessToken);
        localStorage.setItem('user', JSON.stringify(data.user));
        
        onLoginSuccess(data.accessToken);
      } else {
        throw new Error('No access token received');
      }
    } catch (err: any) {
      const errorMsg = err.message || 'Login failed';
      console.error('🚫 Caught error:', errorMsg);
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ display: 'flex', alignItems: 'center', minHeight: '100vh' }}>
      <Box sx={{ width: '100%' }}>
        <Card>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h4" component="h1" sx={{ mb: 1, fontWeight: 'bold', textAlign: 'center' }}>
              🚀 RAAS Semantic Search
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3, textAlign: 'center' }}>
              Microservice - Powered by AI
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs value={loginMode === 'credentials' ? 0 : 1} onChange={(_, newValue) => {
                setLoginMode(newValue === 0 ? 'credentials' : 'token');
                setError(null);
              }}>
                <Tab label="📧 Credentials" />
                <Tab label="🔐 Token" />
              </Tabs>
            </Box>

            {loginMode === 'credentials' ? (
              <Box component="form" onSubmit={handleLogin} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Email or Username"
                  type="text"
                  value={login}
                  onChange={(e) => setLogin(e.target.value)}
                  disabled={loading}
                  placeholder="Enter your email or username"
                />

                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  placeholder="Enter your password"
                />

                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  size="large"
                  type="submit"
                  disabled={loading}
                  sx={{ mt: 2 }}
                  startIcon={loading ? <CircularProgress size={20} /> : null}
                >
                  {loading ? 'Logging in...' : 'Login'}
                </Button>

                <Typography variant="caption" sx={{ textAlign: 'center', color: '#666' }}>
                  💡 Tip: Use your RAAS system credentials
                </Typography>
              </Box>
            ) : (
              <Box component="form" onSubmit={handleLoginWithToken} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  fullWidth
                  label="JWT Token or API Token"
                  type="password"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  disabled={loading}
                  placeholder="Paste your JWT or API token here"
                  multiline
                  rows={4}
                />

                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  size="large"
                  type="submit"
                  disabled={loading}
                  sx={{ mt: 2 }}
                >
                  {loading ? 'Logging in...' : 'Login with Token'}
                </Button>

                <Typography variant="caption" sx={{ textAlign: 'center', color: '#666' }}>
                  💡 Tip: Use a JWT token or API token (64-char hex string)<br/>
                  You can create API tokens in the 'Token-adm' page
                </Typography>
              </Box>
            )}

            
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default Login;
